// react
import React, { useState, useMemo } from "react";

// react-router-dom
import { useNavigate } from "react-router-dom";

// prop-types
import PropTypes from "prop-types";

// react-i18next
import { useTranslation } from "react-i18next";

// context
import { UserAuth } from "context/AuthContext";

// @mui
import { Box, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, TableFooter, TablePagination, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { KeyboardArrowLeftOutlined as KeyboardArrowLeftOutlinedIcon, KeyboardArrowRightOutlined as KeyboardArrowRightOutlinedIcon } from "@mui/icons-material";

// A ---------------------------------------------------------------------- M

const SharedTagsTableToolbar = () => {
  const { conservSostL1 } = UserAuth();
  const { t } = useTranslation();

  return (
    <Grid container alignItems="center" spacing={2} mb={2}>
      <Grid item xs={12} sm={6}>
        <Typography variant="body1" fontWeight={700} color="text.secondary">
          {conservSostL1 ? t("archives") : t("databoxes")}
        </Typography>
      </Grid>
    </Grid>
  );
};

const SharedTagsTableHead = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const headCells = useMemo(
    () => [
      { id: "name", label: t("name") },
      { id: "permissions", label: t("permissions") },
    ],
    [t]
  );

  return (
    <TableHead sx={{ background: `linear-gradient(90deg, ${theme.palette.primary.main}33, ${theme.palette.primary.dark}11)` }}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align={headCell.id !== "actions" ? "left" : "right"} sx={{ fontWeight: 700 }}>
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }) => {
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0} aria-label="previous page">
        {isRtl ? <KeyboardArrowRightOutlinedIcon /> : <KeyboardArrowLeftOutlinedIcon />}
      </IconButton>
      <IconButton onClick={(e) => onPageChange(e, page + 1)} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
        {isRtl ? <KeyboardArrowLeftOutlinedIcon /> : <KeyboardArrowRightOutlinedIcon />}
      </IconButton>
    </Box>
  );
};

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const SharedTagsTable = ({ tags }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - tags.length) : 0;

  return (
    <Box>
      <SharedTagsTableToolbar />

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}CC)`,
          backdropFilter: "blur(6px)",
          border: isMobile ? "none" : `1px solid ${theme.palette.divider}`,
        }}
      >
        <Table>
          {!isMobile && <SharedTagsTableHead />}

          <TableBody>
            {(rowsPerPage > 0 ? tags.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : tags).map((tag) => {
              const permissionsLabel = tag.source === "readonly" ? t("read") : tag.source === "readwrite" ? t("read_write") : "N/A";

              return isMobile ? (
                <Box
                  key={tag.id}
                  onClick={() => navigate(`/${tag.id}`)}
                  sx={{
                    p: 2,
                    mb: 2,
                    mx: 1,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                    cursor: "pointer",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "scale(1.01)",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                    },
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={700}>
                    {tag.name || "N/A"}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {t("permissions")}: {permissionsLabel}
                  </Typography>
                </Box>
              ) : (
                <TableRow key={tag.id} sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#e3f2fd" } }} onClick={() => navigate(`/${tag.id}`)}>
                  <TableCell>{tag.name || "N/A"}</TableCell>

                  <TableCell sx={{ color: "gray" }}>{permissionsLabel}</TableCell>
                </TableRow>
              );
            })}
            {!isMobile && emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={2} />
              </TableRow>
            )}
          </TableBody>

          {!isMobile && (
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[10, 20]}
                  count={tags.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage={t("rows_per_page")}
                  labelDisplayedRows={({ from, to, count }) => t("displayed_rows", { from, to, count: count !== -1 ? count : `more than ${to}` })}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>

        {isMobile && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <TablePaginationActions count={tags.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} />
          </Box>
        )}
      </TableContainer>
    </Box>
  );
};

SharedTagsTable.propTypes = {
  tags: PropTypes.array.isRequired,
};

export default SharedTagsTable;
